/**
 * Error Handler Utilities for AI Button
 *
 * Provides retry logic with exponential backoff and API error code mapping.
 *
 * @module features/ai-button/utils/errorHandler
 * @see SPEC-019 Requirements 13.1, 13.2, 13.3, 13.4, 13.6
 */

import { ApiErrorCode, type ApiError } from '@linkode/shared';

// ============================================================================
// Types
// ============================================================================

/**
 * Options for retry behavior
 */
export interface RetryOptions {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries: number;
  /** Initial delay in milliseconds (default: 500) */
  initialDelay: number;
  /** Maximum delay in milliseconds (default: 5000) */
  maxDelay: number;
  /** Function to determine if an error is retryable */
  isRetryable: (error: Error | ApiError) => boolean;
  /** Optional callback for each retry attempt */
  onRetry?: (attempt: number, delay: number, error: Error | ApiError) => void;
}

/**
 * Result of a retry operation
 */
export interface RetryResult<T> {
  /** Whether the operation succeeded */
  success: boolean;
  /** The result if successful */
  data?: T;
  /** The error if failed */
  error?: Error | ApiError;
  /** Number of attempts made */
  attempts: number;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Default retry options
 */
export const DEFAULT_RETRY_OPTIONS: RetryOptions = {
  maxRetries: 3,
  initialDelay: 500,
  maxDelay: 5000,
  isRetryable: isRetryableError,
};

/**
 * i18n key mapping for API error codes
 * Maps ApiErrorCode to translation keys for user-friendly messages
 *
 * @see Requirements 13.4
 */
export const ERROR_CODE_I18N_MAP: Record<ApiErrorCode, string> = {
  // Auth errors
  [ApiErrorCode.UNAUTHORIZED]: 'errors.unauthorized',
  [ApiErrorCode.FORBIDDEN]: 'errors.forbidden',

  // Resource errors
  [ApiErrorCode.DOC_NOT_FOUND]: 'errors.docNotFound',
  [ApiErrorCode.NOT_FOUND]: 'errors.docNotFound',

  // Validation errors
  [ApiErrorCode.VALIDATION_ERROR]: 'errors.validationError',
  [ApiErrorCode.BAD_REQUEST]: 'errors.validationError',

  // Rate limiting
  [ApiErrorCode.RATE_LIMITED]: 'errors.rateLimited',

  // Infrastructure errors
  [ApiErrorCode.UPSTASH_UNAVAILABLE]: 'errors.upstashUnavailable',
  [ApiErrorCode.EMAIL_SERVICE_UNAVAILABLE]: 'errors.upstashUnavailable',

  // Generic errors
  [ApiErrorCode.INTERNAL_ERROR]: 'errors.internalError',
  [ApiErrorCode.VERSION_CONFLICT]: 'errors.versionConflict',

  // Folder errors
  [ApiErrorCode.FOLDER_NOT_FOUND]: 'errors.docNotFound',
  [ApiErrorCode.FOLDER_ALREADY_EXISTS]: 'errors.validationError',
  [ApiErrorCode.FOLDER_DEPTH_EXCEEDED]: 'errors.validationError',
  [ApiErrorCode.INVALID_FOLDER_NAME]: 'errors.validationError',

  // Document errors
  [ApiErrorCode.DOC_ALREADY_DELETED]: 'errors.docNotFound',

  // AI Apply errors
  [ApiErrorCode.AI_APPLY_VALIDATION_ERROR]: 'errors.aiApplyValidationError',
  [ApiErrorCode.AI_APPLY_FRONTMATTER_PARSE_ERROR]: 'errors.aiApplyFrontmatterParseError',
  [ApiErrorCode.AI_APPLY_FAILED]: 'errors.aiApplyFailed',
};

/**
 * AI-specific error i18n keys
 * Additional mappings for AI Button specific error scenarios
 */
export const AI_ERROR_I18N_MAP: Record<string, string> = {
  network_offline: 'ai.error.offline',
  fetch_cancelled: 'ai.error.cancelled',
  timeout: 'ai.error.timeout',
  unknown: 'ai.error.unknown',
};

// ============================================================================
// Error Classification
// ============================================================================

/**
 * Check if an error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    'requestId' in error
  );
}

/**
 * Check if an error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }
  if (error instanceof Error && error.name === 'NetworkError') {
    return true;
  }
  return false;
}

/**
 * Check if the browser is offline
 */
export function isOffline(): boolean {
  return typeof navigator !== 'undefined' && !navigator.onLine;
}

/**
 * Determine if an error is retryable
 *
 * Retryable errors include:
 * - Network errors (transient connectivity issues)
 * - 5xx server errors (UPSTASH_UNAVAILABLE, INTERNAL_ERROR)
 *
 * Non-retryable errors include:
 * - 4xx client errors (UNAUTHORIZED, FORBIDDEN, VALIDATION_ERROR, etc.)
 * - Rate limiting (should wait, not retry immediately)
 *
 * @see Requirements 13.6
 */
export function isRetryableError(error: Error | ApiError): boolean {
  // Network errors are retryable
  if (isNetworkError(error)) {
    return true;
  }

  // API errors - check status code
  if (isApiError(error)) {
    const code = error.code;

    // 5xx errors are retryable
    if (
      code === ApiErrorCode.UPSTASH_UNAVAILABLE ||
      code === ApiErrorCode.EMAIL_SERVICE_UNAVAILABLE ||
      code === ApiErrorCode.INTERNAL_ERROR ||
      code === ApiErrorCode.AI_APPLY_FAILED
    ) {
      return true;
    }

    // 4xx errors are NOT retryable
    return false;
  }

  // Unknown errors - don't retry
  return false;
}

// ============================================================================
// Retry Logic
// ============================================================================

/**
 * Sleep for a specified duration
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Execute a function with retry logic and exponential backoff
 *
 * @param fn - The async function to execute
 * @param options - Retry options (uses defaults if not provided)
 * @returns Promise resolving to the function result
 * @throws The last error if all retries fail
 *
 * @example
 * ```typescript
 * const result = await withRetry(
 *   () => aiApiClient.getSuggestions(docId),
 *   { maxRetries: 3, initialDelay: 500 }
 * );
 * ```
 *
 * @see Requirements 13.6
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: Partial<RetryOptions> = {}
): Promise<T> {
  const opts: RetryOptions = { ...DEFAULT_RETRY_OPTIONS, ...options };
  let lastError: Error | ApiError;
  let delay = opts.initialDelay;

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error | ApiError;

      // Check if we should retry
      const shouldRetry = attempt < opts.maxRetries && opts.isRetryable(lastError);

      if (!shouldRetry) {
        throw lastError;
      }

      // Notify retry callback
      opts.onRetry?.(attempt + 1, delay, lastError);

      // Wait before retrying
      await sleep(delay);

      // Exponential backoff: double the delay, capped at maxDelay
      delay = Math.min(delay * 2, opts.maxDelay);
    }
  }

  // This should never be reached, but TypeScript needs it
  throw lastError!;
}

/**
 * Execute a function with retry logic and return a result object
 *
 * Unlike `withRetry`, this function never throws. Instead, it returns
 * a result object indicating success or failure.
 *
 * @param fn - The async function to execute
 * @param options - Retry options
 * @returns Promise resolving to a RetryResult object
 *
 * @example
 * ```typescript
 * const result = await withRetryResult(
 *   () => aiApiClient.getSuggestions(docId)
 * );
 *
 * if (result.success) {
 *   console.log('Got suggestions:', result.data);
 * } else {
 *   console.error('Failed after', result.attempts, 'attempts:', result.error);
 * }
 * ```
 */
export async function withRetryResult<T>(
  fn: () => Promise<T>,
  options: Partial<RetryOptions> = {}
): Promise<RetryResult<T>> {
  const opts: RetryOptions = { ...DEFAULT_RETRY_OPTIONS, ...options };
  let attempts = 0;

  try {
    const data = await withRetry(fn, {
      ...opts,
      onRetry: (attempt, delay, error) => {
        attempts = attempt;
        opts.onRetry?.(attempt, delay, error);
      },
    });
    return { success: true, data, attempts: attempts + 1 };
  } catch (error) {
    return {
      success: false,
      error: error as Error | ApiError,
      attempts: attempts + 1,
    };
  }
}

// ============================================================================
// Error Message Helpers
// ============================================================================

/**
 * Get the i18n key for an error
 *
 * @param error - The error to get the i18n key for
 * @returns The i18n translation key
 *
 * @see Requirements 13.4
 */
export function getErrorI18nKey(error: unknown): string {
  // Check for offline state
  if (isOffline()) {
    return AI_ERROR_I18N_MAP.network_offline ?? 'ai.error.unknown';
  }

  // Check for network errors
  if (isNetworkError(error)) {
    return AI_ERROR_I18N_MAP.network_offline ?? 'ai.error.unknown';
  }

  // Check for API errors
  if (isApiError(error)) {
    return ERROR_CODE_I18N_MAP[error.code] ?? AI_ERROR_I18N_MAP.unknown ?? 'ai.error.unknown';
  }

  // Check for abort errors (fetch cancelled)
  if (error instanceof Error && error.name === 'AbortError') {
    return AI_ERROR_I18N_MAP.fetch_cancelled ?? 'ai.error.unknown';
  }

  // Default to unknown error
  return AI_ERROR_I18N_MAP.unknown ?? 'ai.error.unknown';
}

/**
 * Get a user-friendly error message
 *
 * This function returns a fallback message if i18n is not available.
 * In production, use the i18n key from `getErrorI18nKey` instead.
 *
 * @param error - The error to get the message for
 * @returns A user-friendly error message
 */
export function getErrorMessage(error: unknown): string {
  // Check for offline state
  if (isOffline()) {
    return 'You are offline. Please check your internet connection.';
  }

  // Check for network errors
  if (isNetworkError(error)) {
    return 'Network error. Please check your connection and try again.';
  }

  // Check for API errors
  if (isApiError(error)) {
    // Return the API error message (already sanitized by backend)
    return error.message;
  }

  // Check for abort errors
  if (error instanceof Error && error.name === 'AbortError') {
    return 'Request was cancelled.';
  }

  // Default message
  return 'An unexpected error occurred. Please try again.';
}

// ============================================================================
// Export Index
// ============================================================================

export const errorHandler = {
  withRetry,
  withRetryResult,
  isRetryableError,
  isApiError,
  isNetworkError,
  isOffline,
  getErrorI18nKey,
  getErrorMessage,
};
